/**
 * Module dependencies.
 */

import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useGetNotesFileSystemInvalidator } from './use-get-notes-file-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';

/**
 * Note entity types.
 */

export type NoteEntityType = 'project' | 'movement' | 'bankAccount' | 'budget';

/**
 * `NoteLink` (a note linked to an entity).
 */

export type NoteLink = {
  linkId: string;
  noteId: string;
  name: string;
  snippet: string;
  updatedAt: string;
};

/**
 * `NoteBacklink` (an entity a note is linked to).
 */

export type NoteBacklink = {
  linkId: string;
  entityType: NoteEntityType;
  entityId: string;
};

/**
 * Export `useNoteLinks` — notes linked to an entity.
 */

export function useNoteLinks(entityType: NoteEntityType, entityId: string | undefined) {
  const client = useRequestProvider();

  return useQuery<NoteLink[]>(['noteLinks', entityType, entityId], {
    enabled: !!entityId,
    queryFn: async () =>
      client
        .get(resolveAppUrl('appsNotesLinks'), { params: { entityType, entityId } })
        .then(({ data }: { data: any }) => data?.data ?? [])
  });
}

/**
 * Export `useNoteBacklinks` — entities a note is linked to.
 */

export function useNoteBacklinks(noteId: string | null | undefined) {
  const client = useRequestProvider();

  return useQuery<NoteBacklink[]>(['noteBacklinks', noteId], {
    enabled: !!noteId,
    queryFn: async () =>
      client
        .get(resolveAppUrl('appsNotesBacklinks', { interpolations: { id: noteId } }))
        .then(({ data }: { data: any }) => data?.data ?? [])
  });
}

/**
 * Export `useLinkNote` — attach an existing note to an entity.
 */

export function useLinkNote() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      entityType,
      entityId
    }: {
      noteId: string;
      entityType: NoteEntityType;
      entityId: string;
    }) =>
      client
        .post(resolveAppUrl('appsNotesLinks'), { noteId, entityType, entityId })
        .then(({ data }: { data: any }) => data?.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['noteLinks', variables.entityType, variables.entityId]);
      queryClient.invalidateQueries(['noteBacklinks', variables.noteId]);
    }
  });
}

/**
 * Export `useUnlinkNote` — remove a link.
 */

export function useUnlinkNote() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ linkId }: { linkId: string; entityType: NoteEntityType; entityId: string }) =>
      client
        .delete(resolveAppUrl('appsNotesLinkById', { interpolations: { id: linkId } }))
        .then(({ data }: { data: any }) => data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['noteLinks', variables.entityType, variables.entityId]);
      queryClient.invalidateQueries(['noteBacklinks']);
    }
  });
}

/**
 * Export `useCreateLinkedNote` — create a new note already linked to an entity.
 * Returns the created note id.
 */

export function useCreateLinkedNote() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();
  const invalidateTree = useGetNotesFileSystemInvalidator();

  return useMutation({
    mutationFn: async ({
      name,
      entityType,
      entityId
    }: {
      name: string;
      entityType: NoteEntityType;
      entityId: string;
    }): Promise<string> => {
      const note = await client
        .post(resolveAppUrl('appsNotes'), { name, type: 'file' })
        .then(({ data }: { data: any }) => data);
      const noteId = note?.id;

      if (noteId) {
        await client.post(resolveAppUrl('appsNotesLinks'), { noteId, entityType, entityId });
      }

      return noteId;
    },
    onSuccess: (_noteId, variables) => {
      queryClient.invalidateQueries(['noteLinks', variables.entityType, variables.entityId]);
      invalidateTree();
    }
  });
}
